import {type IDictionary} from '../../../@type/dictionary';
import {type FeatureFlag} from '../../../@type/features';
import {type IFeatureSupport} from '../models/CheckoutData';
import {DispatchActionType} from '../models/DispatchActionType';
import {type IEnvironment, type LoadingMode, type ModalPage} from '../models/IEnvironment';
import {type DispatchAction, store} from '../store';
import {createDispatchUpdate} from './initialState';

export function environmentReducer(state: IEnvironment, action: DispatchAction<unknown>): IEnvironment {
	switch (action.type) {
		case DispatchActionType.ENVIRONMENT:
			return {
				...(action as DispatchAction<IEnvironment>).payload,
				plugin: {...(action as DispatchAction<IEnvironment>).payload.plugin},
				modalUI: {...(action as DispatchAction<IEnvironment>).payload.modalUI},
			};
		case DispatchActionType.ENVIRONMENT_TRANSLATED_MODAL_TERMS:
			return {
				...state,
				translated_modal_terms: (action as DispatchAction<IDictionary>).payload,
			};
		case DispatchActionType.ENVIRONMENT_SET_FEATURES:
			return {
				...state,
				plugin: {
					...state.plugin,
					featureSupport: (action as DispatchAction<Record<string, IFeatureSupport | undefined>>).payload,
				},
			};
		default:
			return {
				...state,
				modalUI: {...state.modalUI},
			};
	}
}

// ======================= Action Creators =============================

export function updateEnvironment(options: {
	translated_modal_terms?: IDictionary;
	modalButtonShadow?: boolean;
	modalPageType?: ModalPage;
	modalLoading?: LoadingMode;
}): DispatchAction<IEnvironment> {
	return {
		type: DispatchActionType.ENVIRONMENT,
		payload: {
			translated_modal_terms: options.translated_modal_terms ?? Environment.translated_modal_terms(),
			plugin: {
				featureSupport: store.getState().environment.plugin.featureSupport,
			},
			modalUI: {
				page: (options.modalPageType) ?? Environment.modalUI.page(),
				loadingMode: options.modalLoading ?? Environment.modalUI.loadingMode(),
			},
		},
	};
}

export function setFeatureSupport(features: Record<string, IFeatureSupport | undefined> = {}): DispatchAction<Record<string, IFeatureSupport | undefined>> {
	return {
		type: DispatchActionType.ENVIRONMENT_SET_FEATURES,
		payload: features,
	};
}

export const updateTranslatedModalTerms = createDispatchUpdate<IDictionary>(DispatchActionType.ENVIRONMENT_TRANSLATED_MODAL_TERMS);
export const startModalLoading = () => updateEnvironment({modalLoading: 'loading'});
export const startModalProcessing = () => updateEnvironment({modalLoading: 'processing'});
export const stopModalLoading = () => updateEnvironment({modalLoading: 'finished'});

// ======================= Store Selectors =============================

export const Environment = {
	language: () => document.documentElement.lang ?? 'en-US',
	environment: () => store.getState().environment,
	translated_modal_terms: () => store.getState().environment.translated_modal_terms,
	modalUI: {
		page: () => store.getState().environment.modalUI.page,
		loadingMode: () => store.getState().environment.modalUI.loadingMode,
	},
};

export const Feature = {
	enabled: (flag: FeatureFlag) => store.getState().environment.plugin.featureSupport[flag]?.enabled ?? false,
	metadata: <T>(flag: FeatureFlag, key: string) => (store.getState().environment.plugin.featureSupport[flag]?.metadata?.[key] ?? null) as T | null,
	dynamicMetadata: <T>(flag: FeatureFlag, key: string, cartKey = '0') => (store.getState().calculatedCarts[cartKey]?.feature_metadata?.[flag]?.[key] ?? null) as T | null,
};
